<script setup lang="ts">
import { Head, router, usePage } from "@inertiajs/vue3";
import { computed, ref, toRef } from "vue";
import { toast } from "vue-sonner";

import CreateThreadModal from "@/features/discussion-threads/components/create-thread-modal.vue";
import DiscussionThreadArchiveDialog from "@/features/discussion-threads/components/discussion-thread-archive-dialog.vue";
import DiscussionThreadContent from "@/features/discussion-threads/components/discussion-thread-content.vue";
import DiscussionThreadDetailPanel from "@/features/discussion-threads/components/discussion-thread-detail-panel.vue";
import DiscussionThreadParticipantRemovalDialog from "@/features/discussion-threads/components/discussion-thread-participant-removal-dialog.vue";
import DiscussionThreadSidebar from "@/features/discussion-threads/components/discussion-thread-sidebar.vue";
import DiscussionThreadSummaryCard from "@/features/discussion-threads/components/discussion-thread-summary-card.vue";
import { useDiscussionThreadActions } from "@/features/discussion-threads/composables/use-discussion-thread-actions";
import { useDiscussionThreadListRealtime } from "@/features/discussion-threads/composables/use-discussion-thread-list-realtime";
import { useDiscussionThreadRecovery } from "@/features/discussion-threads/composables/use-discussion-thread-recovery";
import { useDiscussionThreadsLayout } from "@/features/discussion-threads/composables/use-discussion-threads-layout";
import { useDiscussionThreadsSelection } from "@/features/discussion-threads/composables/use-discussion-threads-selection";
import type { DiscussionThreadsPageProps } from "@/features/discussion-threads/types";
import {
  isCartThread,
  threadBadgeDate,
} from "@/lib/discussion-thread-list";
import { organizationDiscussionThreadsPath } from "@/routes";
import type { DiscussionThread, RealtimeDiscussionEvent, SharedData } from "@/types";

defineOptions({ inheritAttrs: false });

const props = defineProps<DiscussionThreadsPageProps>();
const page = usePage<SharedData>();

const organizationId = computed(() => page.props.auth.organization?.id);
const currentUserId = computed(() => page.props.auth.user.id);
const organizationName = computed(
  () => page.props.auth.organization?.name || "Org Demo",
);

const createThreadModalOpen = ref(Boolean(props.create_thread));
const selectedThread = ref<DiscussionThread | null>(props.selected_thread ?? null);
const pendingThreadId = ref<number | null>(null);
const closingInvalidThreadInProgress = ref(false);

const {
  isMobileViewport,
  showDetailButton,
  handleSelectedThreadChange,
  handleSelectedThreadMissing,
  handleThreadClick: applyThreadClickLayout,
  openDetailPanel,
  closeDetailPanel,
  showSidebarPanel,
  sidebarPanelVisible,
  threadPanelVisible,
  detailPanelVisible,
  detailCloseVariant,
  showTabletDetailOverlay,
  detailPanelClass,
  detailCloseLabel,
  chatVariant,
} = useDiscussionThreadsLayout({
  selectedThread,
  hasSelectedThreadInitially: Boolean(props.selected_thread),
});

const closeMissingThreadAndReturn = () => {
  pendingThreadId.value = null;
  handleSelectedThreadMissing();

  if (!organizationId.value) return;
  if (closingInvalidThreadInProgress.value) return;
  if (!page.url.includes("thread_id=")) return;

  closingInvalidThreadInProgress.value = true;
  router.visit(organizationDiscussionThreadsPath(organizationId.value), {
    replace: true,
    preserveScroll: true,
    preserveState: true,
    onFinish: () => {
      closingInvalidThreadInProgress.value = false;
    },
  });
  toast.warning("Ya no tienes acceso a este hilo.");
};

const {
  searchValue,
  selectedMessages,
  selectedMentionables,
  selectedAttachments,
  clearSelectedThreadPayload,
  threadSections,
  selectedParticipantsUsers,
  selectedParticipantsGroups,
  selectedCreatorName,
  selectedCreatorEmail,
  selectedDescription,
  selectedCreatedAt,
  selectedUpdatedAt,
  selectedTitle,
} = useDiscussionThreadsSelection({
  threadsActive: toRef(props, "threads_active"),
  threadsArchived: toRef(props, "threads_archived"),
  selectedThreadProp: toRef(props, "selected_thread"),
  selectedThreadRef: selectedThread,
  messagesThreadIdProp: toRef(props, "messages_thread_id"),
  messagesProp: toRef(props, "messages"),
  mentionablesProp: toRef(props, "mentionables"),
  attachmentsProp: toRef(props, "attachments"),
  onSelectedThreadChange: (incoming, previous) => {
    pendingThreadId.value = null;
    handleSelectedThreadChange(incoming, previous);
  },
  onSelectedThreadMissing: closeMissingThreadAndReturn,
});

const {
  archiveDialogOpen,
  archiveRequestInProgress,
  archiveSwitchDisabled,
  handleArchiveSwitchChange,
  confirmArchiveToggle,
  participantRemovalDialogOpen,
  pendingParticipantRemoval,
  participantRemovalInProgress,
  participantGroupRemovalInProgress,
  participantRemovalDialogDisabled,
  openParticipantRemovalDialog,
  openParticipantGroupRemovalDialog,
  confirmParticipantRemoval,
} = useDiscussionThreadActions({
  organizationId,
  currentUserId,
  selectedThread,
});

const threadHref = (threadId: number) => {
  if (!organizationId.value) return "#";
  return organizationDiscussionThreadsPath(organizationId.value, {
    thread_id: threadId,
  });
};

const selectedSidebarThreadId = computed(
  () => pendingThreadId.value ?? selectedThread.value?.id ?? null,
);
const selectedThreadReloadInProgress = ref(false);
const pendingSelectedThreadReload = ref(false);
const lastSelectedThreadReloadAt = ref(0);
const selectedThreadReloadThrottleMs = 2_000;

const isPrimaryNavigationClick = (event: MouseEvent) =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey &&
  !event.defaultPrevented;

const handleThreadClick = (event: MouseEvent, thread: DiscussionThread) => {
  if (!isPrimaryNavigationClick(event)) return;

  if (selectedThread.value?.id !== thread.id) {
    pendingThreadId.value = thread.id;
    selectedThread.value = thread;
    clearSelectedThreadPayload();
  } else {
    pendingThreadId.value = null;
  }

  applyThreadClickLayout(event, Boolean(organizationId.value));
};

const openCreateThreadModal = () => {
  createThreadModalOpen.value = true;
};

const safeOrganizationId = computed(() => organizationId.value ?? 0);

const reloadSelectedThread = () => {
  if (!selectedThread.value) return;

  if (selectedThreadReloadInProgress.value) {
    pendingSelectedThreadReload.value = true;
    return;
  }

  const now = Date.now();
  if (now - lastSelectedThreadReloadAt.value < selectedThreadReloadThrottleMs) return;

  selectedThreadReloadInProgress.value = true;
  lastSelectedThreadReloadAt.value = now;

  router.reload({
    async: true,
    only: ["selected_thread"],
    preserveScroll: true,
    onFinish: () => {
      selectedThreadReloadInProgress.value = false;
      if (pendingSelectedThreadReload.value) {
        pendingSelectedThreadReload.value = false;
        lastSelectedThreadReloadAt.value = 0;
        reloadSelectedThread();
      }
    },
  });
};

const shouldReloadSelectedThreadForRealtimeEvent = (
  message: RealtimeDiscussionEvent,
): boolean => {
  const activeThreadId = selectedThread.value?.id;
  if (!activeThreadId) return false;

  if (message.type === "discussion.message.created") {
    if (message.thread_id !== activeThreadId) return false;

    const mentionUserIds = message.message.mention_user_ids ?? [];
    const mentionGroupIds = message.message.mention_group_ids ?? [];
    return mentionUserIds.length > 0 || mentionGroupIds.length > 0;
  }

  if (message.type === "discussion.thread.access.changed") {
    if (message.thread_id === null) return true;
    return message.thread_id === activeThreadId;
  }

  return false;
};

useDiscussionThreadRecovery({ getOrganizationId: () => organizationId.value });
useDiscussionThreadListRealtime({
  onRealtimeEvent: (message) => {
    if (!shouldReloadSelectedThreadForRealtimeEvent(message)) return;
    reloadSelectedThread();
  },
});
</script>

<template>
  <Head title="Hilos" />

    <div class="flex h-full min-h-0 w-full flex-col gap-3 bg-muted/50 p-3">
    <div class="relative flex min-h-0 flex-1 gap-3 overflow-hidden">
      <aside
        v-if="sidebarPanelVisible"
        class="flex min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-border bg-background md:w-[280px] md:min-w-[280px]"
      >
        <DiscussionThreadSidebar
          v-model:search-value="searchValue"
          :organization-name="organizationName"
          :sections="threadSections"
          :selected-thread-id="selectedSidebarThreadId"
          :thread-href="threadHref"
          :is-cart-thread="isCartThread"
          :thread-badge-date="threadBadgeDate"
          @create-thread="openCreateThreadModal"
          @thread-click="handleThreadClick"
        />
      </aside>

      <div
        v-if="threadPanelVisible"
        class="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden"
      >
        <DiscussionThreadSummaryCard
          v-if="selectedThread"
          :thread="selectedThread"
          :title="selectedTitle"
          :description="selectedDescription"
          :attachments="selectedAttachments"
          :mentionables="selectedMentionables"
          :available-tags="props.available_tags"
          :organization-id="safeOrganizationId"
          :is-mobile-viewport="isMobileViewport"
          :show-detail-button="showDetailButton"
          @back="showSidebarPanel"
          @open-detail="openDetailPanel"
        />

        <DiscussionThreadContent
          :thread="selectedThread"
          :messages="selectedMessages"
          :mentionables="selectedMentionables"
          :chat-variant="chatVariant"
          thread-section-class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-background"
          empty-state-class="flex flex-1 items-center justify-center overflow-y-auto themed-scrollbar rounded-2xl border border-border bg-background"
          show-action
          @action="openCreateThreadModal"
        />
      </div>

      <aside
        v-if="detailPanelVisible"
        :class="detailPanelClass"
      >
        <DiscussionThreadDetailPanel
          :participants-users="selectedParticipantsUsers"
          :participants-groups="selectedParticipantsGroups"
          :attachments="selectedAttachments"
          :created-at="selectedCreatedAt"
          :updated-at="selectedUpdatedAt"
          :creator-name="selectedCreatorName"
          :creator-email="selectedCreatorEmail"
          :archived="Boolean(selectedThread?.archived)"
          :archive-switch-disabled="archiveSwitchDisabled"
          :participant-removal-in-progress="participantRemovalInProgress"
          :participant-group-removal-in-progress="participantGroupRemovalInProgress"
          :close-variant="detailCloseVariant"
          :close-label="detailCloseLabel"
          @close="closeDetailPanel"
          @remove-user="openParticipantRemovalDialog"
          @remove-group="openParticipantGroupRemovalDialog"
          @archive-change="handleArchiveSwitchChange"
        />
      </aside>

      <div
        v-if="showTabletDetailOverlay"
        class="absolute inset-0 z-10 rounded-2xl bg-black/5"
        @click="closeDetailPanel"
      />
    </div>

    <DiscussionThreadArchiveDialog
      v-model:open="archiveDialogOpen"
      :busy="archiveRequestInProgress"
      @confirm="confirmArchiveToggle"
    />

    <DiscussionThreadParticipantRemovalDialog
      v-model:open="participantRemovalDialogOpen"
      :pending="pendingParticipantRemoval"
      :disabled="participantRemovalDialogDisabled"
      @confirm="confirmParticipantRemoval"
    />

    <CreateThreadModal
      v-if="organizationId"
      v-model:open="createThreadModalOpen"
      :organization-id="organizationId"
      :available-tags="props.available_tags"
      :mentionables="props.mentionables"
      :selected-user-ids="props.create_thread?.selected_user_ids"
      :selected-group-ids="props.create_thread?.selected_group_ids"
      :prefill-mention-text="props.create_thread?.prefill_mention_text"
    />
  </div>
</template>
